/**
 * EF-APP-LOG-VIEWER-V1: Application Log Viewer
 * 
 * Provides administrators with a real-time view of application logs
 * for troubleshooting external integrations (Google Vision OCR, 
 * Google Routes API, SMTP email, etc.)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  FileText,
  RefreshCw,
  Search,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Info,
  Bug,
  Trash2,
  Download,
  Filter,
  Clock,
  Server,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "../lib/utils";

// Log level configuration
const LOG_LEVELS = {
  DEBUG: { color: "text-slate-400 bg-slate-500/10 border-slate-500/20", icon: Bug },
  INFO: { color: "text-blue-400 bg-blue-500/10 border-blue-500/20", icon: Info },
  WARNING: { color: "text-amber-400 bg-amber-500/10 border-amber-500/20", icon: AlertTriangle },
  ERROR: { color: "text-red-400 bg-red-500/10 border-red-500/20", icon: AlertCircle },
};

// Service/Module color coding
const MODULE_COLORS = {
  OCR_SERVICE: "text-purple-400",
  EMAIL_SERVICE: "text-cyan-400",
  INTEGRATIONS: "text-emerald-400",
  API: "text-blue-400",
  GOOGLE_VISION: "text-amber-400",
  SMTP: "text-pink-400",
  EVENTSFLO: "text-primary",
  DEFAULT: "text-muted-foreground",
};

function getModuleColor(module) {
  const upperModule = module?.toUpperCase() || "";
  for (const [key, color] of Object.entries(MODULE_COLORS)) {
    if (upperModule.includes(key)) return color;
  }
  return MODULE_COLORS.DEFAULT;
}

// Log Entry Component
function LogEntryRow({ log, expanded, onToggle }) {
  const LevelIcon = LOG_LEVELS[log.level]?.icon || Info;
  const levelStyle = LOG_LEVELS[log.level]?.color || LOG_LEVELS.INFO.color;
  const moduleColor = getModuleColor(log.module);
  
  // Check if message is multi-line
  const isMultiLine = log.message.includes("\n");
  const displayMessage = expanded ? log.message : log.message.split("\n")[0];
  
  return (
    <>
      <TableRow 
        className={cn(
          "hover:bg-muted/30 cursor-pointer transition-colors",
          log.level === "ERROR" && "bg-red-500/5"
        )}
        onClick={isMultiLine ? onToggle : undefined}
      >
        <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap py-2">
          {log.timestamp}
        </TableCell>
        <TableCell className="py-2">
          <span className={cn("font-mono text-xs", moduleColor)}>
            {log.module}
          </span>
        </TableCell>
        <TableCell className="py-2">
          <Badge variant="outline" className={cn("text-[10px] h-5", levelStyle)}>
            <LevelIcon className="h-3 w-3 mr-1" />
            {log.level}
          </Badge>
        </TableCell>
        <TableCell className="py-2 max-w-[600px]">
          <div className="flex items-start gap-2">
            <span className={cn(
              "text-sm break-all",
              log.level === "ERROR" && "text-red-300"
            )}>
              {expanded ? (
                <pre className="whitespace-pre-wrap font-mono text-xs">{displayMessage}</pre>
              ) : (
                <span className="truncate block">{displayMessage}</span>
              )}
            </span>
            {isMultiLine && (
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 shrink-0">
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function LogViewerPage() {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalLines, setTotalLines] = useState(0);
  const [returnedLines, setReturnedLines] = useState(0);
  
  // Filters
  const [limit, setLimit] = useState(200);
  const [levelFilter, setLevelFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const intervalRef = useRef(null);
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Clear logs dialog
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      if (levelFilter && levelFilter !== "all") {
        params.append("level", levelFilter);
      }
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      
      const response = await api.get(`/debug/logs?${params.toString()}`);
      
      setLogs(response.data.logs || []);
      setTotalLines(response.data.total_lines || 0);
      setReturnedLines(response.data.returned_lines || 0);
      
    } catch (err) {
      console.error("Failed to fetch logs:", err);
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [limit, levelFilter, debouncedSearch]);
  
  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  // Auto-refresh interval
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLogs, refreshInterval * 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchLogs]);
  
  // Toggle row expansion
  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };
  
  // Clear logs
  const handleClearLogs = async () => {
    try {
      setClearing(true);
      await api.post("/debug/logs/clear");
      toast.success("Logs cleared successfully");
      setShowClearDialog(false);
      fetchLogs();
    } catch (err) {
      console.error("Failed to clear logs:", err);
      toast.error("Failed to clear logs");
    } finally {
      setClearing(false);
    }
  };
  
  // Download logs as text file
  const handleDownloadLogs = () => {
    const content = logs.map(log => 
      `${log.timestamp} | ${log.module} | ${log.level} | ${log.message}`
    ).join("\n");
    
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eventsflo-logs-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Logs downloaded");
  };
  
  // Level counts
  const levelCounts = {
    DEBUG: logs.filter(l => l.level === "DEBUG").length,
    INFO: logs.filter(l => l.level === "INFO").length,
    WARNING: logs.filter(l => l.level === "WARNING").length,
    ERROR: logs.filter(l => l.level === "ERROR").length,
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <FileText className="h-7 w-7 text-primary" />
            Application Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            View and troubleshoot application logs for external integrations
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleDownloadLogs}
            disabled={logs.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowClearDialog(true)}
            className="text-red-400 hover:text-red-300"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Logs
          </Button>
          <Button onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="col-span-2">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Log Lines</p>
                <p className="text-2xl font-bold">{totalLines.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Showing {returnedLines} of {totalLines}
                </p>
              </div>
              <Server className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        {Object.entries(LOG_LEVELS).map(([level, config]) => {
          const Icon = config.icon;
          return (
            <Card 
              key={level}
              className={cn(
                "cursor-pointer transition-all",
                levelFilter === level ? "ring-2 ring-primary" : "hover:bg-muted/30"
              )}
              onClick={() => setLevelFilter(levelFilter === level ? "all" : level)}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{level}</p>
                    <p className="text-xl font-bold">{levelCounts[level]}</p>
                  </div>
                  <Icon className={cn("h-6 w-6", config.color.split(" ")[0])} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search log messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="log-search"
              />
            </div>
            
            {/* Level Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="w-[140px]" data-testid="level-filter">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="DEBUG">DEBUG</SelectItem>
                  <SelectItem value="INFO">INFO</SelectItem>
                  <SelectItem value="WARNING">WARNING</SelectItem>
                  <SelectItem value="ERROR">ERROR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Limit */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Limit:</Label>
              <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Auto Refresh */}
            <div className="flex items-center gap-3 ml-auto">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  data-testid="auto-refresh-toggle"
                />
              </div>
              {autoRefresh && (
                <Select value={refreshInterval.toString()} onValueChange={(v) => setRefreshInterval(parseInt(v))}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3s</SelectItem>
                    <SelectItem value="5">5s</SelectItem>
                    <SelectItem value="10">10s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Log Table */}
      <Card>
        <CardContent className="p-0">
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No logs found</p>
              <p className="text-sm">
                {searchTerm || levelFilter !== "all" 
                  ? "Try adjusting your filters" 
                  : "Logs will appear here as the application runs"}
              </p>
            </div>
          ) : (
            <div className="relative">
              {loading && (
                <div className="absolute top-2 right-2 z-10">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              )}
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-[160px]">Timestamp</TableHead>
                      <TableHead className="w-[140px]">Module</TableHead>
                      <TableHead className="w-[100px]">Level</TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => (
                      <LogEntryRow
                        key={index}
                        log={log}
                        expanded={expandedRows.has(index)}
                        onToggle={() => toggleRowExpansion(index)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Clear Logs Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="h-5 w-5" />
              Clear Application Logs
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all application logs. This action cannot be undone.
              <br /><br />
              <strong>Note:</strong> New logs will continue to be recorded after clearing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearLogs}
              disabled={clearing}
              className="bg-red-600 hover:bg-red-700"
            >
              {clearing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Clear All Logs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
